import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    this.#syncAppSlideshowInfinite();
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }

  /**
   * App PDP only: stop Horizon's infinite wrap on the main product media slideshow.
   * slideshow.js reads the `infinite` attribute dynamically during navigation, so
   * removing it is enough. Website galleries keep the attribute.
   *
   * Runs on connect (initial load and after variant replacement via replaceWith)
   * and waits briefly if `html.in-app` is injected after this element connects.
   */
  #syncAppSlideshowInfinite() {
    if (!this.#isMainProductMediaGallery()) return;
    if (!document.body.classList.contains('template-product')) return;

    if (this.#disableInfiniteForApp()) return;

    const observer = new MutationObserver(() => {
      if (this.#disableInfiniteForApp()) observer.disconnect();
    });

    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    this.#controller.signal.addEventListener('abort', () => observer.disconnect(), { once: true });
  }

  /**
   * @returns {boolean}
   */
  #isMainProductMediaGallery() {
    return Boolean(this.closest('.product-information__media'));
  }

  /**
   * @returns {boolean} true when the app condition was applied or is no longer needed
   */
  #disableInfiniteForApp() {
    if (!document.documentElement.classList.contains('in-app')) return false;
    if (!document.body.classList.contains('template-product')) return false;

    const slideshow = this.refs.slideshow ?? this.querySelector('slideshow-component');
    slideshow?.removeAttribute('infinite');
    return true;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
